import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, StatusBadge } from "@/components/DataTable";
import { Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CHANNELS = [
  { key: "in_app", label: "In-app" },
  { key: "email",  label: "Email" },
  { key: "sms",    label: "SMS" },
  { key: "whatsapp", label: "WhatsApp" },
];

const AUDIENCE_OPTIONS = [
  { key: "all_students", label: "All students", audience: {} },
  { key: "fee_defaulters", label: "Fee defaulters", audience: { target: "fee_defaulters" } },
  { key: "all_parents", label: "All parents", audience: { role: "parent" } },
  { key: "all_faculty", label: "All faculty", audience: { role: "faculty" } },
];

export default function BroadcastPage() {
  const [templates, setTemplates] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("custom");
  const [message, setMessage] = useState("");
  const [channels, setChannels] = useState(["in_app"]);
  const [audience, setAudience] = useState("all_students");
  const [sending, setSending] = useState(false);

  const load = async () => {
    const [t, d] = await Promise.all([api.get("/comms/templates"), api.get("/comms/deliveries")]);
    setTemplates(t.data); setDeliveries(d.data);
  };
  useEffect(()=>{load()},[]);

  const applyTemplate = (key) => {
    setSelectedTemplate(key);
    const tpl = templates.find(t => t.key === key);
    if (tpl?.body) setMessage(tpl.body);
  };

  const toggleChannel = (k) => setChannels(prev => prev.includes(k) ? prev.filter(x => x!==k) : [...prev, k]);

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim() || channels.length === 0) return toast.error("Enter message and select at least one channel");
    setSending(true);
    try {
      const audObj = AUDIENCE_OPTIONS.find(a => a.key === audience)?.audience || {};
      const { data } = await api.post("/comms/broadcast", {
        message, template: selectedTemplate, channels, audience: audObj
      });
      toast.success(`Broadcast queued to ${data.recipients} recipients (${data.deliveries} deliveries)`);
      setMessage("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHeader title="Broadcast" subtitle="Send announcements, reminders, and multi-channel messages" />

      <Tabs defaultValue="compose" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="compose" data-testid="broadcast-tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="log" data-testid="broadcast-tab-log">Delivery Log ({deliveries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="compose">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <form onSubmit={send} className="linkedin-card p-5 lg:col-span-2 space-y-4" data-testid="broadcast-form">
              <div>
                <label className="kpi-label">Template</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {templates.map(t => (
                    <button
                      type="button"
                      key={t.key}
                      data-testid={`template-${t.key}`}
                      onClick={()=>applyTemplate(t.key)}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold ${selectedTemplate===t.key ? 'bg-[#0A66C2] text-white' : 'bg-[#EDEBE7] text-[#000000E6] hover:bg-[#E0DFDC]'}`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="kpi-label">Message</label>
                <textarea
                  required
                  data-testid="broadcast-message-input"
                  className="w-full mt-2 px-3 py-2 border border-[#E0DFDC] rounded-md min-h-[140px]"
                  placeholder="Type your message. Use {{name}} for personalization."
                  value={message}
                  onChange={e=>setMessage(e.target.value)}
                />
              </div>

              <div>
                <label className="kpi-label">Channels</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CHANNELS.map(c => (
                    <button
                      type="button"
                      key={c.key}
                      data-testid={`channel-${c.key}`}
                      onClick={()=>toggleChannel(c.key)}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${channels.includes(c.key) ? 'bg-[#EAF3FB] border-[#0A66C2] text-[#0A66C2]' : 'bg-white border-[#E0DFDC] text-[#000000E6] hover:bg-[#F4F2EE]'}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-[#00000099] mt-2">
                  In-app delivers instantly. Email/SMS/WhatsApp deliveries are logged only — external provider not connected.
                </div>
              </div>

              <div>
                <label className="kpi-label">Audience</label>
                <select
                  data-testid="broadcast-audience-select"
                  className="w-full mt-2 px-3 py-2 border border-[#E0DFDC] rounded-md bg-white"
                  value={audience}
                  onChange={e=>setAudience(e.target.value)}
                >
                  {AUDIENCE_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>

              <div className="pt-2 border-t border-[#E0DFDC] flex justify-end">
                <button type="submit" disabled={sending} className="btn-primary inline-flex items-center gap-2" data-testid="broadcast-send-btn">
                  <Send size={14}/> {sending ? "Sending…" : "Send broadcast"}
                </button>
              </div>
            </form>

            <div className="linkedin-card p-5">
              <div className="flex items-center gap-2 mb-3"><Megaphone size={16} className="text-[#0A66C2]"/><h3 className="font-semibold">Preview</h3></div>
              <div className="p-3 rounded-md bg-[#EDEBE7] text-sm text-[#000000E6] whitespace-pre-wrap min-h-[120px]" data-testid="broadcast-preview">
                {message || <span className="text-[#00000066]">Your message will appear here…</span>}
              </div>
              <div className="mt-3 text-xs text-[#00000099]">
                <div>Template: <span className="font-semibold text-[#000000E6]">{selectedTemplate}</span></div>
                <div>Channels: <span className="font-semibold text-[#000000E6]">{channels.join(", ") || "—"}</span></div>
                <div>Audience: <span className="font-semibold text-[#000000E6]">{AUDIENCE_OPTIONS.find(a=>a.key===audience)?.label}</span></div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="log">
          <DataTable
            testid="deliveries-table"
            columns={[
              { key:"created_at", label:"When", render: r => new Date(r.created_at).toLocaleString() },
              { key:"channel", label:"Channel", render: r => <span className="text-[11px] px-2 py-0.5 rounded bg-[#EAF3FB] text-[#0A66C2] uppercase font-semibold">{r.channel}</span> },
              { key:"recipient_name", label:"Recipient" },
              { key:"contact", label:"Contact", render: r => r.channel === "email" ? r.recipient_email : (r.channel === "sms" || r.channel === "whatsapp") ? r.recipient_phone : "—" },
              { key:"template", label:"Template" },
              { key:"message", label:"Message", render: r => <span className="text-xs line-clamp-2 max-w-[280px] inline-block">{r.message}</span> },
              { key:"status", label:"Status", render: r => <StatusBadge status={r.status}/> },
            ]}
            rows={deliveries}
            empty="No deliveries yet"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
